import React, { memo } from 'react';
import { Link } from 'react-router-dom';
import {
  Typography,
  Stack,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Tooltip,
  IconButton,
  Icon
} from 'investira.react.components';
import { PessoaImage } from '../../../../../components/molecules';
import { displays } from 'investira.react.lib';
import { validators, formats } from 'investira.sdk';
import ConcentracaoGraph from './ConcentracaoGraph';

const ConcentracaoDetalhesEmissor = memo(props => {
  const { regra } = props;

  function calcRangeEnquadramento(pMin = 0, pMax = 50) {
    return Math.abs(pMax - pMin);
  }

  function calcAlvoRange(
    pAlvo = null,
    pAlvoLimites = {},
    pDesenquadradoAlvo = null,
    pDesenquadrado = null
  ) {
    let targetColors = 'white';

    if (!validators.isNull(pDesenquadrado)) {
      targetColors = 'error.light';
    }

    if (!validators.isNull(pDesenquadradoAlvo)) {
      targetColors = 'warning.light';
    }

    return {
      alvo: pAlvo,
      alvoLimites: pAlvoLimites,
      alvoWidth: pAlvoLimites?.maximo - pAlvoLimites.minimo,
      desenquadradoAlvo: !validators.isNull(pDesenquadradoAlvo),
      pDesenquadrado: !validators.isNull(pDesenquadrado),
      noAlvo: !validators.isNull(pDesenquadradoAlvo),
      targetColor: targetColors
    };
  }

  function calcObservado(pObservado) {
    if (pObservado <= 0) {
      return '0%';
    }

    return `calc(${pObservado}% - 1px)`;
  }

  return (
    <TableContainer
      id="table-container"
      sx={{
        overflow: 'auto'
      }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell></TableCell>
            <TableCell>Pessoa</TableCell>
            <TableCell align="right">CPNJ</TableCell>
            <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
              Mínimo (%)
            </TableCell>
            <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
              Máximo (%)
            </TableCell>
            <TableCell align="right">Observado</TableCell>
            <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
              Observado %{' '}
            </TableCell>
            <TableCell align="right">Desenquadrado</TableCell>
            <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
              Alvo (%)
            </TableCell>
            <TableCell align="right">Desenquadrado (Alvo)</TableCell>
            <TableCell></TableCell>
            <TableCell align="left">Status</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {regra?.detalhe &&
            regra.detalhe.map(xItem => {
              const hasDesenquadrado = !validators.isNull(xItem.desenquadrado_alvo);

              const hasDesenquadradoAlvo = !validators.isNull(xItem.desenquadrado_alvo);

              const xEnquadramentoRange = calcRangeEnquadramento(xItem.minimo, xItem.maximo);

              const xAlvo = calcAlvoRange(
                xItem.alvo,
                xItem.alvo_limites,
                xItem.desenquadrado_alvo,
                xItem.desenquadrado
              );

              const xObservado = calcObservado(xItem.observado);

              return (
                <React.Fragment key={crypto.randomUUID()}>
                  <TableRow>
                    <TableCell sx={{ width: '24px' }}>
                      {Boolean(xItem?.logo_pessoa_id) && (
                        <PessoaImage
                          logoPessoaId={xItem.logo_pessoa_id}
                          alt={xItem.pessoa_fantasia}
                          hasLogo={Boolean(xItem?.logo_pessoa_id)}
                          size={24}
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      {xItem?.pessoa_fantasia ? (
                        <Typography
                          component={Link}
                          to={`/empresas/${xItem.pessoa_id}`}
                          color="primary"
                          target="_blank"
                          rel="noopener noreferrer"
                          variant="body2"
                          aria-label={`Abrir ${xItem?.ativo} em uma nova aba`}>
                          {xItem.pessoa_fantasia}
                        </Typography>
                      ) : (
                        xItem?.pessoa_fantasia
                      )}
                    </TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                      {displays.cnpj(xItem?.cnpj)}
                    </TableCell>
                    <TableCell align="right">{regra.minimo}</TableCell>
                    <TableCell align="right">{regra.maximo}</TableCell>
                    <TableCell align="right">
                      <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="right"
                        gap={1}
                        sx={{
                          color: xAlvo.targetColor
                        }}>
                        {(xItem.financeiro || xItem.quantidade) &&
                          formats.formatNumber(
                            xItem.financeiro || xItem.quantidade,
                            regra.decimais || 0,
                            true
                          )}
                      </Stack>
                    </TableCell>
                    <TableCell align="right">
                      <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="right"
                        gap={1}
                        sx={{
                          color: xAlvo.targetColor
                        }}>
                        {xItem.observado && formats.formatNumber(xItem.observado, 6, true)}
                      </Stack>
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        color: xAlvo.targetColor
                      }}>
                      {hasDesenquadrado
                        ? formats.formatNumber(xItem.desenquadrado, xItem.decimais || 0, true)
                        : ''}
                    </TableCell>
                    <TableCell align="right" sx={{ color: 'info.main', fontWeight: 600 }}>
                      {regra.alvo}
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        color: xAlvo.targetColor
                      }}>
                      {hasDesenquadradoAlvo
                        ? formats.formatNumber(xItem.desenquadrado_alvo, xItem.decimais || 0, true)
                        : ''}
                    </TableCell>
                    <TableCell align="center">
                      {xItem.observado && (
                        <ConcentracaoGraph
                          range={xEnquadramentoRange}
                          minimo={xItem.minimo}
                          alvo={xAlvo}
                          observado={xObservado}
                          targetColor={xAlvo.targetColor}
                        />
                      )}
                    </TableCell>
                    <TableCell align={xItem.erro ? 'left' : 'center'}>
                      {xItem.erro && (
                        <Tooltip title={xItem.erro} arrow>
                          <IconButton color="warning" size="small">
                            <Icon iconName="warning" size={21} />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              );
            })}
        </TableBody>
      </Table>
    </TableContainer>
  );
});

export default ConcentracaoDetalhesEmissor;
